/**
 * Shared data transformation utilities for visualization charts.
 *
 * Pure functions for filtering, filling gaps, and scaling trade data.
 */

import { groups, index } from "d3";
import { TradeDirection, TradeFlow, ProductClass } from "../types/enums";
import { TradeDirectionSelectorString, ScalingOption } from "../types/vizEnums";
import { computeTotalSumByTradeFlow, computeMonetaryValueByTradeFlow } from "./vizUtils";
import { getLocationQualifiers } from "./locationUtils";
import { currentUSD, constantUSD, perCapita } from "../types/branded";
import type { CurrentUSD, ConstantUSD, PerCapita, ValidatedYearRange } from "../types/branded";
import type { ProductMetadatum } from "../types/metadata";
import type { MetadataFetchState } from "../types/metadataState";
import type { ProductMetadataByLevel } from "../types/metadata";
import type { TradeDataItem } from "../types/tradeData";
import type { TradeObservationBase } from "../types/trade";
import { MetadataMergeType } from "../types/vizEnums";

/** Maps ProductClass enum values to the metadata key used in query responses */
export const PRODUCT_CLASS_METADATA_KEY: Record<string, string> = {
  HS12: "metadata_12",
  HS22: "metadata_22",
  HS92: "metadata_92",
  SITC: "metadata_sitc",
};

// =============================================================================
// Shared filtering helpers
// =============================================================================

/**
 * Structural constraint for data items consumed by transform functions.
 *
 * Extends `TradeObservationBase` with optional overrides (transforms may
 * receive partially populated items) plus `topLevelParent` which is added
 * during enrichment.
 */
export interface TradeDataItemBase extends Partial<TradeObservationBase> {
  topLevelParent?: string;
}

/**
 * Filters trade data by hidden categories and optionally excludes zero/negative values.
 */
export const filterTradeData = <T extends TradeDataItemBase>({
  data,
  hiddenCategories,
  tradeFlow,
  tradeDirection,
  tradeDirectionSelector,
  excludeZeroValues = false,
}: {
  data: T[];
  hiddenCategories: string[];
  tradeFlow?: TradeFlow;
  tradeDirection?: TradeDirection;
  tradeDirectionSelector?: TradeDirectionSelectorString;
  excludeZeroValues?: boolean;
}): T[] => {
  return data.filter((d) => {
    if (d.topLevelParent && hiddenCategories.includes(d.topLevelParent)) return false;
    if (excludeZeroValues && tradeFlow && tradeDirection) {
      const value = computeMonetaryValueByTradeFlow({
        datum: d,
        tradeFlow,
        tradeDirection,
        tradeDirectionSelector,
      });
      if (!value || value <= 0) return false;
    }
    return true;
  });
};

/**
 * Generic helper to fill in missing year observations with zero values.
 */
export function fillMissingYearObservations<T extends { year: number }>({
  data,
  groupKey,
  yearRange,
}: {
  data: T[];
  groupKey: keyof T;
  yearRange: ValidatedYearRange;
}): T[] {
  const { start: startYear, end: endYear } = yearRange;
  const result: T[] = [];

  const grouped = groups(
    data,
    (d: T) => d[groupKey],
    (d: T) => d.year,
  );

  grouped.forEach(([_entityId, yearsData]: [unknown, [number, T[]][]]) => {
    let prototype: T | undefined;

    yearsData.forEach((yearDatum: [number, T[]], i: number) => {
      result.push(...yearDatum[1]);
      if (i === 0) {
        prototype = { ...yearDatum[1][0] };
      }
    });

    const lookup = index(yearsData, (d: [number, T[]]) => d[0]);

    for (let y = startYear; y <= endYear; y++) {
      if (!lookup.has(y)) {
        result.push({
          ...prototype!,
          year: y,
          exportValue: 0,
          importValue: 0,
        } as T);
      }
    }
  });

  return result;
}

// =============================================================================
// Trade Value Scaling (for overtime visualizations)
// =============================================================================

// ScalingOption is now defined in vizEnums.ts and re-exported here for backward compatibility
export { ScalingOption } from "../types/vizEnums";

/** Trade value pair for a specific monetary scaling. */
export interface CurrentTradeValuePair {
  exportValue: CurrentUSD;
  importValue: CurrentUSD;
}

export interface ConstantTradeValuePair {
  exportValue: ConstantUSD;
  importValue: ConstantUSD;
}

export interface PerCapitaTradeValuePair {
  exportValue: PerCapita<CurrentUSD>;
  importValue: PerCapita<CurrentUSD>;
}

export interface PerCapitaConstantTradeValuePair {
  exportValue: PerCapita<ConstantUSD>;
  importValue: PerCapita<ConstantUSD>;
}

export interface ScaledTradeValues {
  [ScalingOption.Current]: CurrentTradeValuePair;
  [ScalingOption.Constant]: ConstantTradeValuePair;
  [ScalingOption.PerCapita]: PerCapitaTradeValuePair;
  [ScalingOption.PerCapitaConstant]: PerCapitaConstantTradeValuePair;
}

/**
 * Computes scaled trade values for different Y-axis options (overtime charts).
 */
export function computeScaledTradeValues({
  data,
  deflator,
  population,
  tradeFlow,
}: {
  data: TradeDataItemBase[];
  deflator?: number;
  population?: number;
  tradeFlow: TradeFlow;
}): ScaledTradeValues {
  const tradeValues = {
    [ScalingOption.Current]: { exportValue: 0, importValue: 0 },
    [ScalingOption.Constant]: { exportValue: 0, importValue: 0 },
    [ScalingOption.PerCapita]: { exportValue: 0, importValue: 0 },
    [ScalingOption.PerCapitaConstant]: { exportValue: 0, importValue: 0 },
  } as ScaledTradeValues;

  if (!data || data.length === 0) {
    return tradeValues;
  }

  const sumExportValue =
    computeTotalSumByTradeFlow({
      data,
      tradeFlow,
      tradeDirection: TradeDirection.Exports,
    }) ?? 0;

  const sumImportValue =
    computeTotalSumByTradeFlow({
      data,
      tradeFlow,
      tradeDirection: TradeDirection.Imports,
    }) ?? 0;

  tradeValues[ScalingOption.Current] = {
    exportValue: currentUSD(sumExportValue),
    importValue: currentUSD(sumImportValue),
  };

  if (deflator) {
    tradeValues[ScalingOption.Constant] = {
      exportValue: constantUSD(sumExportValue / deflator),
      importValue: constantUSD(sumImportValue / deflator),
    };
  }

  if (population) {
    tradeValues[ScalingOption.PerCapita] = {
      exportValue: perCapita<CurrentUSD>(sumExportValue / population),
      importValue: perCapita<CurrentUSD>(sumImportValue / population),
    };
  }

  if (deflator && population) {
    const divisor = deflator * population;
    tradeValues[ScalingOption.PerCapitaConstant] = {
      exportValue: perCapita<ConstantUSD>(sumExportValue / divisor),
      importValue: perCapita<ConstantUSD>(sumImportValue / divisor),
    };
  }

  return tradeValues;
}

/** Hierarchical variant for location-based overtime transforms. */
export interface HierarchicalScaledTradeValues {
  [ScalingOption.Current]: {
    exportValue: CurrentUSD | undefined;
    importValue: CurrentUSD | undefined;
  };
  [ScalingOption.Constant]: {
    exportValue: ConstantUSD | undefined;
    importValue: ConstantUSD | undefined;
  };
}

export function computeScaledTradeValuesHierarchical({
  data,
  deflator,
  tradeFlow,
  scalingOption,
}: {
  data: TradeDataItemBase[];
  deflator?: number;
  tradeFlow: TradeFlow;
  scalingOption?: string;
}): HierarchicalScaledTradeValues {
  const tradeValues = {
    [ScalingOption.Current]: { exportValue: undefined, importValue: undefined },
    [ScalingOption.Constant]: {
      exportValue: undefined,
      importValue: undefined,
    },
  } as HierarchicalScaledTradeValues;

  const sumExportValue = computeTotalSumByTradeFlow({
    data,
    tradeFlow,
    tradeDirection: TradeDirection.Exports,
    scalingOption: scalingOption,
  });
  const sumImportValue = computeTotalSumByTradeFlow({
    data,
    tradeFlow,
    tradeDirection: TradeDirection.Imports,
    scalingOption: scalingOption,
  });

  tradeValues[ScalingOption.Current] = {
    exportValue: sumExportValue !== undefined ? currentUSD(sumExportValue) : undefined,
    importValue: sumImportValue !== undefined ? currentUSD(sumImportValue) : undefined,
  };

  if (deflator) {
    tradeValues[ScalingOption.Constant] = {
      exportValue:
        sumExportValue !== undefined ? constantUSD(sumExportValue / deflator) : undefined,
      importValue:
        sumImportValue !== undefined ? constantUSD(sumImportValue / deflator) : undefined,
    };
  }

  return tradeValues;
}

// =============================================================================
// Product & location resolution helpers
// =============================================================================

/**
 * Resolves the flat array of product metadata for a given product class.
 */
export const getProductsForClass = (
  productClass: ProductClass,
  metadataFetches: Record<string, MetadataFetchState<ProductMetadataByLevel>>,
): ProductMetadatum[] | null => {
  const classToKey: Record<string, string> = {
    [ProductClass.HS92Products]: "hs92",
    [ProductClass.HS12Products]: "hs12",
    [ProductClass.HS22Products]: "hs22",
    [ProductClass.SITCProducts]: "sitc",
  };
  const key = classToKey[productClass];
  if (!key) return null;
  const state = metadataFetches[key];
  if (!state || state.status !== "success") return null;
  const metadata = state.data;
  const { section, twoDigit, fourDigit, sixDigit } = metadata;
  return [...section, ...twoDigit, ...fourDigit, ...(sixDigit || [])];
};

/**
 * Determines which of exporter/importer to use as the reference location.
 */
export const determineReferenceLocation = (
  exporter: string | undefined,
  importer: string | undefined,
): string | undefined => {
  const { exporterIsWorld, exporterIsUndefined, importerIsWorld, importerIsUndefined } =
    getLocationQualifiers({ exporter, importer });

  if (!exporterIsUndefined && !importerIsUndefined) {
    if (!exporterIsWorld && importerIsWorld) return exporter;
    if (!importerIsWorld && exporterIsWorld) return importer;
    return exporter;
  }
  if (
    (exporterIsWorld && importerIsUndefined) ||
    (importerIsWorld && exporterIsUndefined)
  ) {
    return exporterIsWorld ? exporter : importer;
  }
  return exporter;
};

// =============================================================================
// Top-level parent merging
// =============================================================================

/**
 * Merges trade data items with their top-level parent (region or sector).
 *
 * For regions: maps each partner country to its containing region group.
 * For sectors: maps each product to its top-level parent product.
 */
export const mergeDataWithTopLevelParent = <T extends TradeDataItem>({
  data,
  mergeType,
  regionsMetadata,
  productsMetadata,
}: {
  data: T[];
  mergeType: MetadataMergeType;
  regionsMetadata?: Array<{ groupId?: string; members?: (string | number)[] }>;
  productsMetadata?: Array<{ productId: string; topParent?: { productId: string } | null }>;
}): (T & { topLevelParent?: string })[] => {
  let dataMergedWithTopLevelParent: (T & { topLevelParent?: string })[];
  if (mergeType === MetadataMergeType.Region && regionsMetadata !== undefined) {
    const memberToGroup = new Map<string | number, string>();
    for (const region of regionsMetadata) {
      if (region.groupId && region.members) {
        for (const member of region.members) {
          memberToGroup.set(member, region.groupId);
        }
      }
    }
    dataMergedWithTopLevelParent = data
      .map((datum) => ({ ...datum, topLevelParent: memberToGroup.get(datum.partnerCountryId as number) }))
      .filter((datum): datum is T & { topLevelParent: string } => datum.topLevelParent !== undefined);
  } else if (
    mergeType === MetadataMergeType.Sector &&
    productsMetadata !== undefined
  ) {
    const productIdLookup = index(productsMetadata, (d) => d.productId);
    dataMergedWithTopLevelParent = data.map((datum) => {
      const productId = datum.productId as string;
      let findMatchingMetadata = productIdLookup.get(productId);
      let topLevelParent;
      if (findMatchingMetadata) {
        topLevelParent = findMatchingMetadata.topParent?.productId;
      } else {
        topLevelParent = undefined;
      }

      return {
        ...datum,
        topLevelParent,
      };
    });
  } else {
    dataMergedWithTopLevelParent = data;
  }

  return dataMergedWithTopLevelParent;
};

